// src/pages/languages/LanguageCategoryPlans.jsx
import { useState, useEffect, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowRight,
  Plus,
  Edit2,
  Trash2,
  X,
  CreditCard,
  Check,
} from "lucide-react";
import {
  languageCategoriesAPI,
  languagePlansAPI,
  getApiError,
} from "../../services/languageService";

// نفس القيم الموجودة في LanguageSubscriptionPlan.PLAN_CHOICES في الباك اند
const PLAN_TYPES = {
  MONTHLY: { label: "شهر واحد", defaultDays: 30 },
  QUARTERLY: { label: "ثلاثة أشهر", defaultDays: 90 },
};

const emptyForm = {
  plan_type: "MONTHLY",
  price: "",
  duration_days: PLAN_TYPES.MONTHLY.defaultDays,
  description: "",
  is_active: true,
};

export default function LanguageCategoryPlans() {
  const { categoryId } = useParams();
  const [plans, setPlans] = useState([]);
  const [categoryName, setCategoryName] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [modal, setModal] = useState(null); // { plan: null | plan }
  const [form, setForm] = useState(emptyForm);

  const fetchPlans = useCallback(async () => {
    try {
      setLoading(true);
      const data = await languagePlansAPI.listAll(categoryId);
      setPlans(data.plans || []);
    } catch (e) {
      setError(getApiError(e, "تعذر تحميل الخطط"));
    } finally {
      setLoading(false);
    }
  }, [categoryId]);

  useEffect(() => {
    fetchPlans();
    languageCategoriesAPI
      .getOne(categoryId)
      .then((d) => setCategoryName(d?.name || d?.category?.name || ""))
      .catch(() => {});
  }, [categoryId, fetchPlans]);

  const isEditing = !!modal?.plan;
  const usedTypes = plans.map((p) => p.plan_type);
  const availableTypes = Object.keys(PLAN_TYPES).filter(
    (t) => !usedTypes.includes(t)
  );

  const openCreate = () => {
    const type = availableTypes[0];
    if (!type) return;
    setForm({
      ...emptyForm,
      plan_type: type,
      duration_days: PLAN_TYPES[type].defaultDays,
    });
    setError("");
    setModal({ plan: null });
  };

  const openEdit = (plan) => {
    setForm({
      plan_type: plan.plan_type,
      price: plan.price,
      duration_days: plan.duration_days,
      description: plan.description || "",
      is_active: plan.is_active,
    });
    setError("");
    setModal({ plan });
  };

  const handleTypeChange = (type) =>
    setForm((f) => ({
      ...f,
      plan_type: type,
      duration_days: PLAN_TYPES[type].defaultDays,
    }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    const price = Number(form.price);
    const days = Number(form.duration_days);
    if (form.price === "" || Number.isNaN(price) || price < 0) {
      setError("أدخل سعرًا صحيحًا");
      return;
    }
    if (!Number.isInteger(days) || days <= 0) {
      setError("مدة الاشتراك لازم تكون رقم صحيح أكبر من صفر");
      return;
    }

    try {
      setSaving(true);
      if (isEditing) {
        // plan_type مش بيتعدل من الباك اند
        await languagePlansAPI.update(modal.plan.id, {
          price,
          duration_days: days,
          description: form.description,
          is_active: form.is_active,
        });
        setNotice("تم تحديث الخطة بنجاح");
      } else {
        await languagePlansAPI.create(categoryId, {
          plan_type: form.plan_type,
          price,
          duration_days: days,
          description: form.description,
          is_active: form.is_active,
        });
        setNotice("تم إنشاء الخطة بنجاح");
      }
      setModal(null);
      fetchPlans();
    } catch (err) {
      setError(getApiError(err, "حدث خطأ أثناء الحفظ"));
    } finally {
      setSaving(false);
    }
  };

  // الباك اند بيعطّل الخطة بس (مش حذف فعلي) لأنها مربوطة باشتراكات
  const handleDeactivate = async (plan) => {
    if (!window.confirm("سيتم تعطيل هذه الخطة ولن تظهر للطلاب. متابعة؟")) return;
    try {
      await languagePlansAPI.deactivate(plan.id);
      setNotice("تم تعطيل الخطة بنجاح");
      fetchPlans();
    } catch (err) {
      alert(getApiError(err, "حدث خطأ أثناء التعطيل"));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <Link
            to={`/dashboard/languages/categories/${categoryId}/skills`}
            className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-2"
          >
            <ArrowRight className="w-4 h-4" />
            رجوع للمهارات
          </Link>
          <h1 className="text-2xl font-bold text-gray-900">
            خطط الدفع {categoryName ? `— ${categoryName}` : ""}
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            أسعار اشتراك الطلاب في هذه الكاتيجوري
          </p>
        </div>

        <button
          onClick={openCreate}
          disabled={availableTypes.length === 0}
          title={availableTypes.length === 0 ? "كل أنواع الخطط موجودة بالفعل" : ""}
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg transition-colors"
        >
          <Plus className="w-5 h-5" />
          <span>إضافة خطة</span>
        </button>
      </div>

      {notice && (
        <div className="bg-green-50 border border-green-200 text-green-700 rounded-xl px-4 py-3 text-sm flex items-center justify-between">
          <span>{notice}</span>
          <button onClick={() => setNotice("")} className="font-medium underline">
            إغلاق
          </button>
        </div>
      )}

      {!modal && error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm">
          {error}
        </div>
      )}

      {/* Plans */}
      {plans.length === 0 ? (
        <div className="card text-center py-16">
          <CreditCard className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-bold text-gray-900 mb-2">لا توجد خطط</h3>
          <p className="text-gray-500 text-sm mb-6">
            أضف خطة اشتراك عشان الطلاب يقدروا يشتركوا في الكاتيجوري
          </p>
          <button
            onClick={openCreate}
            className="inline-flex items-center gap-2 bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
          >
            <Plus className="w-4 h-4" />
            إضافة خطة جديدة
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl">
          {plans.map((plan) => (
            <div
              key={plan.id}
              className={`card hover:shadow-lg transition-shadow ${
                plan.is_active ? "" : "opacity-60"
              }`}
            >
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-bold text-gray-900">
                  {PLAN_TYPES[plan.plan_type]?.label || plan.name}
                </h3>
                <span
                  className={`text-xs font-semibold px-2.5 py-1 rounded-full ${
                    plan.is_active
                      ? "bg-green-100 text-green-700"
                      : "bg-gray-100 text-gray-600"
                  }`}
                >
                  {plan.is_active ? "نشطة" : "معطّلة"}
                </span>
              </div>

              <p className="text-3xl font-bold text-blue-600 mb-1">
                {plan.price} <span className="text-base font-medium">ريال</span>
              </p>
              <p className="text-sm text-gray-500 mb-3">
                مدة الاشتراك: {plan.duration_days} يوم
              </p>
              {plan.description && (
                <p className="text-sm text-gray-600 mb-4">{plan.description}</p>
              )}

              <div className="flex gap-2">
                <button
                  onClick={() => openEdit(plan)}
                  className="flex-1 flex items-center justify-center gap-1 py-2 rounded-lg bg-blue-50 hover:bg-blue-100 text-blue-600 text-sm transition-colors"
                >
                  <Edit2 className="w-4 h-4" />
                  تعديل
                </button>
                {plan.is_active && (
                  <button
                    onClick={() => handleDeactivate(plan)}
                    className="flex items-center justify-center gap-1 px-3 py-2 rounded-lg bg-red-50 hover:bg-red-100 text-red-600 text-sm transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                    تعطيل
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <form
            onSubmit={handleSubmit}
            className="bg-white rounded-2xl w-full max-w-md p-6 space-y-4 shadow-xl"
          >
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold text-gray-900">
                {isEditing ? "تعديل الخطة" : "إضافة خطة جديدة"}
              </h2>
              <button
                type="button"
                onClick={() => setModal(null)}
                className="p-1 rounded-lg hover:bg-gray-100"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-3 py-2 text-sm">
                {error}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                نوع الخطة
              </label>
              <select
                value={form.plan_type}
                onChange={(e) => handleTypeChange(e.target.value)}
                disabled={isEditing}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 disabled:bg-gray-100"
              >
                {(isEditing ? [form.plan_type] : availableTypes).map((t) => (
                  <option key={t} value={t}>
                    {PLAN_TYPES[t].label}
                  </option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  السعر (ريال)
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.price}
                  onChange={(e) => setForm({ ...form, price: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  المدة (بالأيام)
                </label>
                <input
                  type="number"
                  min="1"
                  step="1"
                  value={form.duration_days}
                  onChange={(e) =>
                    setForm({ ...form, duration_days: e.target.value })
                  }
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                الوصف (اختياري)
              </label>
              <input
                type="text"
                maxLength={200}
                value={form.description}
                onChange={(e) =>
                  setForm({ ...form, description: e.target.value })
                }
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
              />
            </div>

            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
              <input
                type="checkbox"
                checked={form.is_active}
                onChange={(e) =>
                  setForm({ ...form, is_active: e.target.checked })
                }
                className="w-4 h-4"
              />
              الخطة نشطة (تظهر للطلاب)
            </label>

            <div className="flex gap-2 pt-2">
              <button
                type="submit"
                disabled={saving}
                className="flex-1 flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white py-2 rounded-lg transition-colors"
              >
                <Check className="w-4 h-4" />
                {saving ? "جاري الحفظ..." : "حفظ"}
              </button>
              <button
                type="button"
                onClick={() => setModal(null)}
                className="px-4 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700"
              >
                إلغاء
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}